import ApiService from '@/services/apiService';
import { mapState } from 'vuex';
import iconsMixin from '../dtwin/state/bridge/iconsMixin';
import { get, debounce } from 'lodash';
export default {
  name: 'DeviceTemplateAddWizard',
  mixins: [iconsMixin],
  props: {
    isOpen: {
      type: Boolean,
      default: false
    }
  },
  data: function () {
    return {
      accounts: [],
      formValid: false,
      deviceDriversLoading: false,
      createServerRequest: false,
      template: {},
      accountName: '',
      deviceTemplatesTotalCount: 0,
      deviceTemplatesLimit: 0,
      deviceTemplateLimitExceeded: false,
      requiredRule: [v => !!v || this.$i18n_t('Common.fieldRequired')],
      selectedBridge: null,
      deviceBridgesForAccount: [],
      bridgeTypes: [],
      deviceBridgesLoading: false,
      deviceDriversData: {
        deviceDrivers: [],
        driverSearch: null,
        searchTerm: null,
        currentPage: 0,
        driversHasNextPage: true
      }
    };
  },
  computed: {
    ...mapState('app', ['inpOutlined', 'inpFilled', 'image', 'color', 'UiTheme', 'permissions'])
  },
  watch: {
    'template.tenantId'() {
      this.fetchTenantById();
      this.fetchParents();
      this.fetchNextDrivers();
      this.fetchBridges();
    },
    'deviceDriversData.driverSearch': debounce(async function (newValue, oldValue) {
      if (newValue === oldValue) {
        return;
      }
      this.deviceDriversData = {
        deviceDrivers: [],
        driverSearch: newValue,
        searchTerm: newValue,
        currentPage: 0,
        driversHasNextPage: true
      };
      await this.fetchNextDrivers();
    }, 200)
  },
  created() {
    this.fetchAccounts();
  },
  methods: {
    closeDialog() {
      this.template = {};
      this.$emit('close');
    },
    fetchAccounts() {
      this.$store.dispatch('app/loadAccounts').then(accounts => {
        this.accounts = accounts.filter(t => !t.isRootTenant);
      }).catch(error => {
        console.error(error);
        this.$snack(this.$i18n_t('NiotaClassic.Devices.snacks.errorFindingAccounts'), 'error');
      });
    },
    fetchParents() {
      ApiService.digitalTwinFetchChildren(this, null, 0, 1, null, ['bare'], false, this.template.tenantId).then(result => {
        this.template.parentId = get(result, 'digitaltwins[0].id', null);
      }).catch(error => {
        console.error('Error fetching parents', error);
      });
    },
    save() {
      this.createServerRequest = true;
      this.template.deviceDriverVersion = 'default';
      this.template.connectorId = this.selectedBridge.id;
      ApiService.deviceTemplateCreate(this.template).then(res => {
        this.$emit('created', res);
        this.$snack(this.$i18n_t('DigitalTwin.VirtualDevice.deviceTemplates.snacks.createSuccess'), 'success');
      }).catch(error => {
        if (error.response && this.$i18n_te(`Error.${error.response.data.message}`)) {
          this.$snack(this.$i18n_t(`Error.${error.response.data.message}`), 'error');
        } else {
          this.$snack(this.$i18n_t('DigitalTwin.VirtualDevice.deviceTemplates.snacks.createError'), 'error');
        }
      }).finally(() => {
        this.createServerRequest = false;
        this.template = {};
      });
    },
    parentSelectorFilter(item) {
      return item.tenantId === this.template.tenantId;
    },
    async endDriversIntersect(entries, observer, isIntersecting) {
      if (isIntersecting) {
        await this.fetchNextDrivers();
      }
    },
    async fetchNextDrivers() {
      if (!this.deviceDriversData.driversHasNextPage || this.deviceDriversData.isLoading) {
        return;
      }
      try {
        const DRIVER_PAGE_SIZE = 10;
        this.deviceDriversData.isLoading = true;
        const moreDrivers = await ApiService.virtualDeviceDrivers.fetch({
          fields: 'id,title',
          offset: this.deviceDriversData.currentPage,
          limit: DRIVER_PAGE_SIZE,
          search: this.deviceDriversData.searchTerm
        });
        this.deviceDriversData.currentPage += DRIVER_PAGE_SIZE;
        this.deviceDriversData.deviceDrivers = [...this.deviceDriversData.deviceDrivers, ...moreDrivers.deviceDrivers];
        if (this.deviceDriversData.deviceDrivers.length >= moreDrivers.totalCount) {
          this.deviceDriversData.driversHasNextPage = false;
        }
      } catch (error) {
        console.error(error);
        this.$snack(this.$i18n_t('DigitalTwin.VirtualDevice.wizard.fetchDriverError'), 'error');
      } finally {
        this.deviceDriversData.isLoading = false;
      }
    },
    fetchBridges() {
      this.selectedBridge = null;
      this.deviceBridgesForAccount = [];
      if (!this.template || !this.template.tenantId) {
        return;
      }
      this.deviceBridgesLoading = true;
      Promise.all([ApiService.getBridgeTypes().then(bridgeTypes => {
        return bridgeTypes.filter(type => {
          return Object.keys(get(type, 'manifest.commands.instance', {})).length > 0;
        });
      }), ApiService.getBridgesForTenant(this.template.tenantId)]).then(results => {
        this.bridgeTypes = results[0];
        let validDeviceBridges = results[1].filter(bridgeInstance => {
          const bridgeTypeDef = results[0].find(bridgeDef => bridgeDef.type === bridgeInstance.type);
          if (get(bridgeTypeDef, 'manifest.functionScope.extension.devices.create') || get(bridgeTypeDef, 'manifest.functionScope.extension.devices.read')) {
            return true;
          }
          return false;
        });
        this.deviceBridgesForAccount = validDeviceBridges;
        this.deviceBridgesLoading = false;
      }).catch(error => {
        this.deviceBridgesLoading = false;
        this.$snack(this.$i18n_t('Connectors.Connectors.errors.connectionError'), 'error');
        console.error('Error fetching bridges', error);
      });
    },
    bridgeConfig(bridge) {
      if (!bridge) {
        return;
      }
      const foundMatch = this.bridgeTypes.find(config => {
        return config.type === bridge.type;
      });
      return foundMatch;
    },
    fetchTenantById() {
      return ApiService.tenantGetById(this.template.tenantId, ['no-scope-descendents']).then(async tenant => {
        try {
          this.accountName = tenant.name;
          await this.getTemplatesLimit();
        } catch (error) {
          console.error('Error getting template limit', error);
        }
      }).catch(error => {
        console.error('Error fetching tenant', error);
      });
    },
    async getTemplatesLimit() {
      const templatesLimit = await ApiService.getTemplatesLimit(this.template.tenantId);
      const accountTemplates = await ApiService.deviceTemplatesFetch({
        page: 1,
        pageSize: 1,
        sortDesc: true,
        tenantIds: [this.template.tenantId]
      });
      this.deviceTemplatesTotalCount = accountTemplates.pagination['total-count'];
      this.deviceTemplatesLimit = get(templatesLimit, 'data.templatesLimit.templateLimit');
      if (parseInt(this.deviceTemplatesTotalCount) >= parseInt(this.deviceTemplatesLimit)) {
        this.deviceTemplateLimitExceeded = true;
      }
    }
  }
};